// Form fields shared by the create dialog's steps and the Settings tab (spec 6.4 Create, 6.13).
import { Link } from 'react-router';
import type {
  BlueprintDocument,
  CloudAccount,
  OnFailure,
  ParameterDefinition,
  Provider,
  SecretStore,
  VaultMode,
} from '../api/types';
import { Field, FieldMessages } from '../components/ui';
import { describedBy } from '../lib/a11y';
import { strings } from '../strings';
import { managedVaultLabel } from '../variables/registry';
import type { SettingsForm } from './settingsForm';

const c = strings.createDeployment;

export function RegionSelect({
  id,
  provider,
  value,
  onChange,
  invalid,
  describedById,
  label,
}: {
  id: string;
  provider: Provider | undefined;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  describedById?: string;
  label?: string;
}) {
  if (!provider) {
    return (
      <input
        id={id}
        className="input"
        value={value}
        aria-label={label}
        aria-invalid={invalid || undefined}
        aria-describedby={describedById}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <select
      id={id}
      className="input"
      value={value}
      aria-label={label}
      aria-invalid={invalid || undefined}
      aria-describedby={describedById}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{strings.common.notSet}</option>
      {value && !provider.regions.some((r) => r.name === value) && <option value={value}>{value}</option>}
      {provider.regions.map((region) => (
        <option key={region.name} value={region.name}>
          {region.displayName} ({region.name})
        </option>
      ))}
    </select>
  );
}

export function ParameterField({
  id,
  name,
  definition,
  value,
  errors,
  onChange,
}: {
  id: string;
  name: string;
  definition: ParameterDefinition;
  value: string;
  errors: string[];
  onChange: (value: string) => void;
}) {
  const required = definition.default === undefined;
  const help = [
    definition.description,
    definition.default !== undefined ? strings.settings.defaultValue(String(definition.default)) : null,
    definition.minimum !== undefined || definition.maximum !== undefined
      ? strings.settings.range(definition.minimum, definition.maximum)
      : null,
  ]
    .filter(Boolean)
    .join(' ');
  const aria = {
    'aria-invalid': errors.length ? true : undefined,
    'aria-describedby': describedBy(id, !!help, errors.length > 0),
  };
  let input;
  if (definition.allowed?.length || definition.type === 'boolean') {
    const options = definition.allowed?.length ? definition.allowed.map(String) : ['true', 'false'];
    input = (
      <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)} {...aria}>
        <option value="">{required ? '' : strings.common.notSet}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  } else {
    input = (
      <input
        id={id}
        className="input"
        type={definition.type === 'number' ? 'number' : 'text'}
        min={definition.minimum}
        max={definition.maximum}
        value={value}
        placeholder={definition.default !== undefined ? String(definition.default) : undefined}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        {...aria}
      />
    );
  }
  return (
    <Field label={name} htmlFor={id} help={help || undefined} errors={errors} required={required}>
      {input}
    </Field>
  );
}

function verification(account: CloudAccount): string {
  if (!account.lastVerification) return c.neverVerified;
  return account.lastVerification.succeeded ? c.verified : c.verificationFailed;
}

/** One cloud-account select per provider in use, with each account's verification state (create step 2). */
export function AccountFields({
  idPrefix,
  providers,
  used,
  accounts,
  form,
  setForm,
  errorsAt,
  admin,
}: {
  idPrefix: string;
  providers: Provider[];
  used: readonly string[];
  accounts: CloudAccount[];
  form: SettingsForm;
  setForm: (update: (form: SettingsForm) => SettingsForm) => void;
  errorsAt: (pointer: string) => string[];
  admin: boolean;
}) {
  if (used.length === 0) return <p className="help">{c.noProvidersYet}</p>;
  return (
    <>
      <p className="help mb-2">{c.providerAccounts}</p>
      {used.map((provider) => {
        const options = accounts.filter((a) => a.provider === provider);
        const pointer = `/settings/cloudAccounts/${provider}`;
        const errors = errorsAt(pointer);
        const id = `${idPrefix}-account-${provider}`;
        return (
          <Field
            key={provider}
            label={providers.find((p) => p.key === provider)?.displayName ?? provider}
            htmlFor={id}
            errors={errors}
            help={options.length === 0 ? strings.settings.noAccounts(provider) : undefined}
            required
          >
            <select
              id={id}
              className="input"
              value={form.cloudAccounts[provider] ?? ''}
              aria-invalid={errors.length ? true : undefined}
              aria-describedby={describedBy(id, options.length === 0, errors.length > 0)}
              onChange={(e) =>
                setForm((f) => ({ ...f, cloudAccounts: { ...f.cloudAccounts, [provider]: e.target.value } }))
              }
            >
              <option value="">{strings.settings.chooseAccount}</option>
              {options.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} ({verification(account)})
                </option>
              ))}
            </select>
          </Field>
        );
      })}
      {admin && (
        <Link className="link text-sm" to="/cloud-accounts">
          {c.newAccount}
        </Link>
      )}
    </>
  );
}

/** "Secrets go to a managed Azure Key Vault in westeurope." or the store fallback; null without a provider. */
function vaultSentence(provider: Provider | undefined, region: string): string | null {
  if (!provider) return null;
  return provider.supportsManagedVault
    ? c.managedVault(managedVaultLabel(provider.key), region || strings.common.notSet.toLowerCase())
    : c.storeFallback(provider.displayName);
}

/**
 * Where secret values live (feature spec 15 9.8; spec 6.4 create step 4, 6.13 item 6): the managed vault or the
 * secret store, shown when the effective vault provider `supportsManagedVault`. `effective` is the mode in effect
 * when the form holds none (the deployment's `effectiveVaultMode`, or the dry run's filled mode when creating);
 * `limitation` is the server's message when the managed vault can't deliver the blueprint (the store is then
 * preselected with "chosen for you").
 */
export function VaultModeField({
  idPrefix,
  form,
  update,
  providers,
  effective,
  limitation,
  fixedText,
  storeWarning,
  errors,
}: {
  idPrefix: string;
  form: SettingsForm;
  update: (patch: Partial<SettingsForm>) => void;
  providers: Provider[];
  effective: VaultMode | null;
  limitation?: string | null;
  /** A fixed Azure vault: the managed option reads "Managed vault · fixed" with this sentence. */
  fixedText?: string | null;
  /** The store option's `VAULT_SECRETS_WILL_BE_DELETED` sentence (a live namespace vault). */
  storeWarning?: { text: string; danger: boolean } | null;
  errors: string[];
}) {
  const custom = form.vaultPlacementChoice === 'custom';
  const providerKey = (custom ? form.vaultProvider : '') || form.defaultProvider;
  const provider = providers.find((p) => p.key === providerKey);
  if (!provider?.supportsManagedVault) return null;
  const region =
    (custom ? form.vaultRegion : '') || form.defaultRegion || strings.common.notSet.toLowerCase();
  const chosen = form.vaultMode ?? effective ?? 'managed';
  const label = managedVaultLabel(provider.key);
  const v = strings.vaults;
  return (
    <fieldset className="mb-3" data-testid="vault-mode-field">
      <legend className="label">{v.modeLegend}</legend>
      <label className="mb-1 flex items-start gap-2 text-sm">
        <input
          type="radio"
          className="mt-1"
          name={`${idPrefix}-secrets-mode`}
          value="managed"
          checked={chosen === 'managed'}
          onChange={() => update({ vaultMode: 'managed' })}
        />
        <span>
          <span className="font-medium">{fixedText ? v.managedFixed : v.managedOption(label, region)}</span>
          <span className="help block">{fixedText ?? v.managedHelp}</span>
          {limitation && <span className="field-error block">{limitation}</span>}
        </span>
      </label>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="radio"
          className="mt-1"
          name={`${idPrefix}-secrets-mode`}
          value="store"
          checked={chosen === 'store'}
          onChange={() => update({ vaultMode: 'store' })}
        />
        <span>
          <span className="font-medium">{v.storeOption(form.secretStore || 'default')}</span>
          {limitation && chosen === 'store' && !form.vaultMode && (
            <span className="badge tone-info ml-2" data-testid="chosen-for-you">
              {v.chosenForYou}
            </span>
          )}
          {storeWarning && (
            <span
              className={`help block ${storeWarning.danger ? 'text-danger' : ''}`}
              data-testid="store-warning"
            >
              {storeWarning.text}
            </span>
          )}
        </span>
      </label>
      <FieldMessages messages={errors} />
    </fieldset>
  );
}

/** The vault placement of create step 4 and of the Settings tab before the vault exists (spec 6.4, 6.13). */
export function VaultFields({
  idPrefix,
  form,
  update,
  providers,
  errorsAt,
  appsElsewhere,
}: {
  idPrefix: string;
  form: SettingsForm;
  update: (patch: Partial<SettingsForm>) => void;
  providers: Provider[];
  errorsAt: (pointer: string) => string[];
  appsElsewhere?: string | null;
}) {
  const providerOf = (key: string) => providers.find((p) => p.key === key);
  const custom = form.vaultPlacementChoice === 'custom';
  const effectiveProvider = custom ? form.vaultProvider || form.defaultProvider : form.defaultProvider;
  const effectiveRegion = custom ? form.vaultRegion || form.defaultRegion : form.defaultRegion;
  const sentence = vaultSentence(providerOf(effectiveProvider), effectiveRegion);
  return (
    <fieldset className="mb-3">
      <legend className="label">{c.vaultPlacement}</legend>
      {(['default', 'custom'] as const).map((mode) => (
        <label key={mode} className="mb-1 flex items-center gap-2 text-sm">
          <input
            type="radio"
            name={`${idPrefix}-vault-mode`}
            checked={form.vaultPlacementChoice === mode}
            onChange={() =>
              update(
                mode === 'custom'
                  ? {
                      vaultPlacementChoice: 'custom',
                      vaultProvider: form.vaultProvider || form.defaultProvider,
                      vaultRegion: form.vaultRegion || form.defaultRegion,
                    }
                  : { vaultPlacementChoice: 'default' },
              )
            }
          />
          {mode === 'default' ? c.sameAsDefault : c.chooseVault}
        </label>
      ))}
      {custom && (
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <Field
            label={strings.settings.vaultProvider}
            htmlFor={`${idPrefix}-vault-provider`}
            errors={errorsAt('/settings/vault/provider')}
          >
            <select
              id={`${idPrefix}-vault-provider`}
              className="input"
              value={form.vaultProvider}
              onChange={(e) => update({ vaultProvider: e.target.value, vaultRegion: '' })}
            >
              <option value="">{strings.settings.noProvider}</option>
              {providers.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label={strings.settings.vaultRegion}
            htmlFor={`${idPrefix}-vault-region`}
            errors={errorsAt('/settings/vault/region')}
          >
            <RegionSelect
              id={`${idPrefix}-vault-region`}
              provider={providerOf(form.vaultProvider || form.defaultProvider)}
              value={form.vaultRegion}
              onChange={(value) => update({ vaultRegion: value })}
            />
          </Field>
        </div>
      )}
      {sentence && (
        <p className="help" data-testid="vault-mode">
          {sentence}
        </p>
      )}
      {appsElsewhere && <p className="help">{appsElsewhere}</p>}
      <p className="help">{c.vaultRule}</p>
      {!custom && (
        <FieldMessages
          messages={[...errorsAt('/settings/vault/provider'), ...errorsAt('/settings/vault/region')]}
        />
      )}
      <FieldMessages messages={errorsAt('/settings/vault')} />
    </fieldset>
  );
}

export function SecretStoreField({
  id,
  value,
  stores,
  onChange,
  errors,
}: {
  id: string;
  value: string;
  stores: SecretStore[];
  onChange: (value: string) => void;
  errors: string[];
}) {
  return (
    <Field label={strings.settings.secretStore} htmlFor={id} errors={errors}>
      <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="default">{strings.settings.defaultStore}</option>
        {stores.map((store) => (
          <option key={store.name} value={store.name}>
            {store.name} ({store.kind})
          </option>
        ))}
      </select>
    </Field>
  );
}

export function FailurePolicyField({
  name,
  value,
  onChange,
  errors,
}: {
  name: string;
  value: OnFailure;
  onChange: (value: OnFailure) => void;
  errors: string[];
}) {
  return (
    <fieldset>
      <legend className="label">{strings.settings.onFailure}</legend>
      {(['stop', 'rollback'] as OnFailure[]).map((policy) => (
        <label key={policy} className="mb-1 flex items-start gap-2 text-sm">
          <input
            type="radio"
            name={name}
            className="mt-1"
            checked={value === policy}
            onChange={() => onChange(policy)}
          />
          <span>
            <span className="font-medium">
              {policy === 'stop' ? strings.settings.stop : strings.settings.rollback}
            </span>
            <span className="help block">
              {policy === 'stop' ? strings.settings.stopHelp : strings.settings.rollbackHelp}
            </span>
          </span>
        </label>
      ))}
      <FieldMessages messages={errors} />
    </fieldset>
  );
}

/** Per-resource placement overrides (create step 4's Advanced). */
export function OverridesTable({
  idPrefix,
  doc,
  form,
  setForm,
  providers,
  errorsAt,
}: {
  idPrefix: string;
  doc: BlueprintDocument;
  form: SettingsForm;
  setForm: (update: (form: SettingsForm) => SettingsForm) => void;
  providers: Provider[];
  errorsAt: (pointer: string) => string[];
}) {
  const providerOf = (key: string) => providers.find((p) => p.key === key);
  return (
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">{strings.settings.resource}</th>
            <th scope="col">{strings.settings.provider}</th>
            <th scope="col">{strings.settings.region}</th>
          </tr>
        </thead>
        <tbody>
          {doc.resources.map((resource) => {
            const value = form.overrides[resource.id] ?? { provider: '', region: '' };
            const pointer = `/settings/placementOverrides/${resource.id}`;
            const effective = value.provider || resource.placement?.provider || form.defaultProvider;
            const set = (next: { provider: string; region: string }) =>
              setForm((f) => ({ ...f, overrides: { ...f.overrides, [resource.id]: next } }));
            return (
              <tr key={resource.id}>
                <th scope="row" className="font-mono font-normal">
                  {resource.id}
                </th>
                <td>
                  <select
                    id={`${idPrefix}-ovp-${resource.id}`}
                    className="input"
                    aria-label={strings.deploymentSettings.providerOf(resource.id)}
                    value={value.provider}
                    onChange={(e) => set({ provider: e.target.value, region: '' })}
                  >
                    <option value="">{strings.common.notSet}</option>
                    {providers
                      .filter((p) => p.supportedTypes.includes(resource.type))
                      .map((p) => (
                        <option key={p.key} value={p.key}>
                          {p.displayName}
                        </option>
                      ))}
                  </select>
                  <FieldMessages messages={errorsAt(`${pointer}/provider`)} />
                </td>
                <td>
                  <RegionSelect
                    id={`${idPrefix}-ovr-${resource.id}`}
                    label={strings.deploymentSettings.regionOf(resource.id)}
                    provider={providerOf(effective)}
                    value={value.region}
                    onChange={(region) => set({ ...value, region })}
                  />
                  <FieldMessages messages={[...errorsAt(`${pointer}/region`), ...errorsAt(pointer)]} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
